import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '../../app/logger';
import {
  canCaptureSetPiece,
  closeSetPiece,
  commitSetPiece,
  loadSetPieceContext,
  newSetPieceDraft,
} from '../../app/setPiece';
import { cycleMarkerAt, placeMarker } from '../../domain/setPiece';
import type { BoxPoint } from '../../domain/setPiece';
import { resumeDraftB } from './setPieceB';
import type { MatchRow, SetPieceRow, SetPieceScheme, SetPieceType } from '../../types';

/**
 * B-12 — the set-piece sheet's controller: state, the draft write path and the load, with no
 * rendering (the B-9/B-10 split: decisions live where a Node suite reaches them — `setPieceB.ts`
 * and the engine; the rendered sheet is verified by driving a real browser,
 * `scripts-b/verify-b-setpiece.mjs`).
 *
 * ── Every capture decision is imported, none re-derived ─────────────────────
 *
 * `newSetPieceDraft` stamps the minute once (SES-10, through the one total derivation);
 * `placeMarker`/`cycleMarkerAt` own the marker vocabulary (`MARKER_CYCLE`);
 * `commitSetPiece`/`closeSetPiece` are the only writes; `canCaptureSetPiece` is the same
 * `isInMatch` the handler enforces. **The match row is the SHELL's** (B-10's discipline: one
 * authority, re-read after every committed transition) — this hook loads only the captures list,
 * for SES-3's draft resume.
 *
 * ── Compose synchronously, render on commit (SES-2) ─────────────────────────
 *
 * Two marker taps 80 ms apart must not both build their row from the same *rendered* state, or
 * the second write erases the first marker — so composition reads the `pending` **ref**, which
 * advances at the tap. Rendering moves the other way: the marker appears only in `onCommitted`
 * (the bus's native-`complete` seam), because on this sheet the marker appearing under the
 * finger IS the whole acknowledgment, and rendering it optimistically would show a marker the
 * handler may yet refuse. A's browser pass caught exactly that defect (three markers in the DOM,
 * two in storage) — the split is the fix, kept.
 */

export type SetPieceBPhase = 'loading' | 'ready' | 'failed';

export interface SetPieceBState {
  readonly phase: SetPieceBPhase;
  /** The capture being marked up, or `null` when none is open. Renders only what committed. */
  readonly draft: SetPieceRow | null;
  /** `false` before kickoff — the sheet renders, starts are declined, and it says why. */
  readonly capturing: boolean;
  readonly notice: string | null;
}

const MODULE = 'ui-b/setPiece';

export function useSetPieceB(match: MatchRow) {
  const [phase, setPhase] = useState<SetPieceBPhase>('loading');
  const [draft, setDraft] = useState<SetPieceRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** The authoritative in-flight draft — what the NEXT command composes on (see the header). */
  const pending = useRef<SetPieceRow | null>(null);
  const loaded = useRef(false);

  /** Storage wins: resets both the rendered draft and the row the next command composes on. */
  const resetTo = useCallback((row: SetPieceRow | null): void => {
    pending.current = row;
    setDraft(row);
  }, []);

  // ── Load: SES-3's resume, from the data ────────────────────────────────────

  /**
   * Re-reads the captures and adopts the newest unfinished one. Two callers: entry (resume is
   * found in the committed `draft` column, never a session record) and a refused write (the
   * rendered capture then describes something storage disagrees with — storage wins).
   */
  const refresh = useCallback(async (): Promise<void> => {
    const context = await loadSetPieceContext(match.id);
    if (context === undefined) {
      // A vanished match is a cascade's normal outcome — the shell's own reload navigates away;
      // the sheet just declines to render a capture for it.
      setPhase('failed');
      setNotice('That match no longer exists.');
      return;
    }
    resetTo(resumeDraftB(context.captures));
    setPhase('ready');
  }, [match.id, resetTo]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void refresh().catch((cause: unknown) => {
      log.exception(MODULE, 'B-12: the set-piece sheet could not be loaded', cause);
      setPhase('failed');
      setNotice('The set-piece sheet could not be loaded. The log has the detail.');
    });
  }, [refresh]);

  // ── The one write path ─────────────────────────────────────────────────────

  /**
   * Advances the capture: compose synchronously, commit, **render on commit** — §2.4's "every
   * marker tap commits the whole draft row". A rejection re-reads and re-adopts (the next tap
   * must not build on a row storage refused); a thrown storage failure surfaces and logs.
   */
  const mutate = useCallback(
    (next: SetPieceRow): void => {
      pending.current = next;
      void commitSetPiece(next, () => {
        setDraft(next);
      }).then(
        (result) => {
          if (result.ok) return;
          setNotice(result.message);
          void refresh().catch((cause: unknown) => {
            log.exception(MODULE, 'B-12: the sheet could not be re-read after a refused write', cause);
          });
        },
        (cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause);
          setNotice(message);
          log.error(MODULE, 'B-12: a set-piece write failed and was rolled back', { message });
        },
      );
    },
    [refresh],
  );

  // ── The capture actions (§2.4's sentence, one function each) ───────────────

  /**
   * Start a capture of `type` — `newSetPieceDraft` stamps the minute **once, now** (SES-10: a
   * corner started at 62′ is a 62′ corner even if the markers take until 63′). A capture already
   * open is closed first (two open drafts would make SES-3's resume ambiguous); the close is not
   * awaited — the bus serializes, so it commits before the new draft's first write — and not
   * swallowed: a failed close leaves that second draft, which only the log would ever show.
   */
  const start = useCallback(
    (type: SetPieceType): void => {
      if (!canCaptureSetPiece(match)) return;
      const open = pending.current;
      if (open !== null) {
        void closeSetPiece(match.id, open.id).then(
          (result) => {
            if (!result.ok) {
              log.error(MODULE, 'B-12: the previous capture could not be closed', {
                id: open.id,
                code: result.code,
                message: result.message,
              });
            }
          },
          (cause: unknown) => {
            log.exception(MODULE, 'B-12: closing the previous capture threw', cause);
          },
        );
      }
      mutate(newSetPieceDraft(match, type, Date.now()));
    },
    [match, mutate],
  );

  /** *"Tap drops a marker"* — at the engine-normalized point (`diagramActionB` resolved it). */
  const drop = useCallback(
    (point: BoxPoint): void => {
      const open = pending.current;
      if (open === null) return;
      mutate({ ...open, markers: placeMarker(open.type, open.markers, point) });
    },
    [mutate],
  );

  /** A repeat tap on a marker re-classifies it through `MARKER_CYCLE`; it never moves. */
  const cycle = useCallback(
    (index: number): void => {
      const open = pending.current;
      if (open === null) return;
      mutate({ ...open, markers: cycleMarkerAt(open.type, open.markers, index) });
    },
    [mutate],
  );

  /**
   * The tap-only way out of a mis-placed marker. The repeat-tap cycle re-classifies but never
   * removes, so without this a wrong drop is permanent — on a surface tapped by a person
   * watching a match, a correction path is table stakes, not gold-plating (LIVE-6/LIVE-5's
   * reasoning, and A ruled the same control non-speculative).
   */
  const clearMarkers = useCallback((): void => {
    const open = pending.current;
    if (open === null || open.markers.length === 0) return;
    mutate({ ...open, markers: [] });
  }, [mutate]);

  /** §2.4's scheme picker — tapping the current value clears it (the column is nullable). */
  const setScheme = useCallback(
    (scheme: SetPieceScheme): void => {
      const open = pending.current;
      if (open === null) return;
      mutate({ ...open, scheme: open.scheme === scheme ? null : scheme });
    },
    [mutate],
  );

  /**
   * §2.4 — *"Done → `closeSetPiece`"*. Resolves `true` when the sheet may close; a failed close
   * keeps the sheet open with the reason (leaving on a failed close would strand a draft that
   * reappears on the next entry with no explanation). No open capture closes nothing —
   * `CloseSetPiece` is idempotent engine-side, and the sheet has nothing to send.
   */
  const finish = useCallback(async (): Promise<boolean> => {
    const open = pending.current;
    if (open === null) return true;
    const result = await closeSetPiece(match.id, open.id);
    if (!result.ok) {
      setNotice(result.message);
      return false;
    }
    resetTo(null);
    return true;
  }, [match.id, resetTo]);

  const state: SetPieceBState = {
    phase,
    draft,
    capturing: canCaptureSetPiece(match),
    notice,
  };

  return {
    state,
    actions: {
      start,
      drop,
      cycle,
      clearMarkers,
      setScheme,
      finish,
      dismissNotice: () => {
        setNotice(null);
      },
    },
  };
}

export type SetPieceBActions = ReturnType<typeof useSetPieceB>['actions'];
